import { differenceInDays, parseISO } from 'date-fns';

export interface BookWithDates {
  id: string;
  started_at?: string;
  finished_at?: string;
  books?: {
    id: string;
    title: string;
    authors: string[];
    page_count?: number;
    subjects?: string[];
  };
}

export interface ReadingSpeedData {
  averagePagesPerDay: number;
  totalReadingDays: number;
  fastestBook?: {
    title: string;
    authors: string[];
    pagesPerDay: number;
    totalDays: number;
  };
  slowestBook?: {
    title: string;
    authors: string[];
    pagesPerDay: number;
    totalDays: number;
  };
}

export interface GenreSpeedData {
  genre: string;
  averageDays: number;
  bookCount: number;
  totalPages: number;
}

export interface AuthorSpeedData {
  author: string;
  averageDays: number;
  bookCount: number;
  fastestBook: string;
  fastestDays: number;
}

/**
 * Calculate reading speed (pages per day) for a single book
 */
export function calculateReadingSpeed(book: BookWithDates): number | null {
  if (!book.started_at || !book.finished_at || !book.books?.page_count) {
    return null;
  }

  const days = differenceInDays(parseISO(book.finished_at), parseISO(book.started_at));
  if (days <= 0) return null;

  return book.books.page_count / days;
}

/**
 * Calculate reading duration in days for a single book
 */
export function getReadingDuration(book: BookWithDates): number | null {
  if (!book.started_at || !book.finished_at) return null;

  const days = differenceInDays(parseISO(book.finished_at), parseISO(book.started_at));
  return days > 0 ? days : null;
}

/**
 * Calculate overall reading speed statistics
 */
export function calculateReadingSpeedStats(books: BookWithDates[]): ReadingSpeedData {
  const booksWithSpeed = books
    .map(book => ({
      book,
      speed: calculateReadingSpeed(book),
      days: getReadingDuration(book),
    }))
    .filter(item => item.speed !== null && item.days !== null && item.book.books?.page_count) as Array<{
      book: BookWithDates;
      speed: number;
      days: number;
    }>;

  if (booksWithSpeed.length === 0) {
    return {
      averagePagesPerDay: 0,
      totalReadingDays: 0,
    };
  }

  // Calculate average pages per day
  const totalPages = booksWithSpeed.reduce((sum, item) => sum + (item.book.books!.page_count || 0), 0);
  const totalDays = booksWithSpeed.reduce((sum, item) => sum + item.days, 0);
  const averagePagesPerDay = totalDays > 0 ? totalPages / totalDays : 0;

  // Find fastest book (highest pages per day)
  const fastest = booksWithSpeed.reduce((max, item) => 
    item.speed > max.speed ? item : max
  );

  // Find slowest book (lowest pages per day)
  const slowest = booksWithSpeed.reduce((min, item) => 
    item.speed < min.speed ? item : min
  );

  return {
    averagePagesPerDay: Math.round(averagePagesPerDay),
    totalReadingDays: totalDays,
    fastestBook: {
      title: fastest.book.books!.title,
      authors: fastest.book.books!.authors || [],
      pagesPerDay: Math.round(fastest.speed),
      totalDays: fastest.days,
    },
    slowestBook: {
      title: slowest.book.books!.title,
      authors: slowest.book.books!.authors || [],
      pagesPerDay: Math.round(slowest.speed),
      totalDays: slowest.days,
    },
  };
}

/**
 * Calculate average reading speed grouped by genre
 */
export function groupByGenreSpeed(books: BookWithDates[]): GenreSpeedData[] {
  const genreMap = new Map<string, { totalDays: number; bookCount: number; totalPages: number }>();

  books.forEach(book => {
    const days = getReadingDuration(book);
    if (!days || !book.books?.subjects || !book.books.page_count) return;

    book.books.subjects.forEach(genre => {
      const current = genreMap.get(genre) || { totalDays: 0, bookCount: 0, totalPages: 0 };
      genreMap.set(genre, {
        totalDays: current.totalDays + days,
        bookCount: current.bookCount + 1,
        totalPages: current.totalPages + book.books!.page_count!,
      });
    });
  });

  return Array.from(genreMap.entries())
    .map(([genre, data]) => ({
      genre,
      averageDays: Math.round(data.totalDays / data.bookCount),
      bookCount: data.bookCount,
      totalPages: data.totalPages,
    }))
    .filter(item => item.bookCount >= 2) // Only show genres with 2+ books
    .sort((a, b) => a.averageDays - b.averageDays)
    .slice(0, 8);
}

/**
 * Calculate average reading speed grouped by author
 */
export function groupByAuthorSpeed(books: BookWithDates[]): AuthorSpeedData[] {
  const authorMap = new Map<string, { 
    totalDays: number; 
    bookCount: number; 
    books: Array<{ title: string; days: number }> 
  }>();

  books.forEach(book => {
    const days = getReadingDuration(book);
    if (!days || !book.books?.authors || !book.books.title) return;

    book.books.authors.forEach(author => {
      const current = authorMap.get(author) || { totalDays: 0, bookCount: 0, books: [] };
      current.books.push({ title: book.books!.title, days });
      authorMap.set(author, {
        totalDays: current.totalDays + days,
        bookCount: current.bookCount + 1,
        books: current.books,
      });
    });
  });

  return Array.from(authorMap.entries())
    .map(([author, data]) => {
      const fastestBook = data.books.reduce((min, book) => 
        book.days < min.days ? book : min
      );
      
      return {
        author,
        averageDays: Math.round(data.totalDays / data.bookCount),
        bookCount: data.bookCount,
        fastestBook: fastestBook.title,
        fastestDays: fastestBook.days,
      };
    })
    .filter(item => item.bookCount >= 2) // Only show authors with 2+ books
    .sort((a, b) => a.averageDays - b.averageDays)
    .slice(0, 5);
}
